import React, { useEffect, useMemo, useState } from 'react'
import { Box, Text } from 'rebass'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import qs from 'query-string'
import { useNewTranslation } from 'hooks/index'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'

import {
    selectCampaign,
    selectCampaignPerformance,
    selectRequestsAreLoading,
    createGetRequestErrors,
    makeSelectCampaignPerformanceData,
} from 'state'
import {
    updateSubmissionPerformance,
    paySubmission,
    rejectSubmissionPayout,
    SUBMISSION_STATUS_ENUM,
} from 'state/submissions'
import {
    getCampaignPerformance,
    clearCampaignPerformance,
    getCampaign,
    exportCampaignPerformance,
    getCampaignPerformanceFiltersRequest,
} from 'state/campaigns'
import { getTasksList, clearTasksList, selectTasksPayoutColumns, selectTasks } from 'state/tasks'
import { STATE_KEYS, clearResponse } from 'state/requests'
import {
    MAX_PAGE_SIZE,
    selectActivePageNum,
    selectSearchTerm,
    selectPartiposterTagsParam,
    setPartiposterTagFilter,
} from 'state/review'
import { theme } from 'theme'
import { selectCMUserUuid } from 'state/profile'
import { selectConstants, selectCountrySetting } from 'state/constants'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { TagCell } from 'components/Shared/MuiTable/Cell/TagCell'
import PerformanceActionCell from 'components/Shared/MuiTable/Cell/PerformanceActionCell'

import { DeleteTagModal } from 'components/Tag/TagModals'
import { PageHeader } from '.'
import ExportModal from 'components/Submissions/Review/common/ExportModal'
import { requestProfileOptions } from 'state/constants'
import { TableViewType } from 'components/Shared/MuiTable'
import ListApprovePayoutPopUp from './ListApprovePayoutPopUp'
import { PLATFORM_ENUM } from 'services/api/constants'
import { getInfoTooltipHeaderCell } from 'components/Shared/MuiTable/Cell/InfoTooltipHeaderCell'
import { useShowPrivateCampaignBudget } from 'hooks/permissions'
import { usePermission, useCheckSomePermissions } from 'hooks/permissions'
import { CampaignSubmission, Payout } from 'components/Shared/Permission/enums'
import { clearSubmissionFilters, setTeamCountryByCampaign } from 'state/filter'
import TagHistoryModal from 'components/Tag/HistoryModal'
import TagAddModal from 'components/Tag/AddModal'
import { useAddCustomizeFilter } from 'hooks/displayTooltip'
import { EarningAdjustmentCell } from 'components/Shared/MuiTable/Cell/EarningAdjustmentCell'
import { AdjustEarningModal, PaidOutsideAppModal } from './PayoutModal'
import { GridColDefProps } from './Review/Submissions/SimpleTable/constants'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'

interface PayoutsProps {
    location: any
}
export interface MassPayoutData {
    name: string
    submissionId: string
}
const Payouts: React.FC<PayoutsProps> = ({ location }) => {
    const [t] = useNewTranslation()
    const [editingSubmissionPerformance, setEditingSubmissionPerformance] = useState<any>({
        submissionUuid: '',
        data: [],
    })
    const [, , handleClosePartiposterTags] = useAddCustomizeFilter()
    const dispatch = useDispatch()
    const params = qs.parse(location.search)
    const { campaign: campaignUuid } = params
    //this is to check if profile is loaded
    const profileUuid = useSelector(selectCMUserUuid)
    const campaign = useSelector(selectCampaign(campaignUuid as string))
    const countrySetting = useSelector(selectCountrySetting(campaign && campaign.country))
    const currencySign = countrySetting ? countrySetting.currency.sign : '$'
    const campaignPerformance = useSelector(selectCampaignPerformance)
    const tasks = useSelector(selectTasks)
    const { campaign: campaignSetting } = useSelector(selectConstants)
    const campaignPlatforms = useMemo(() => {
        return _.uniq(tasks.map(task => task.platform))
    }, [tasks])

    const isLoading = useSelector(
        selectRequestsAreLoading([STATE_KEYS.Campaign, STATE_KEYS.TasksList, STATE_KEYS.CampaignPerformance]),
    )
    const isUpdateLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.UpdateSubmissionPerformance]))
    const isExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportPerformance]))
    const hasShowPrivateCampaignBudget = useShowPrivateCampaignBudget(campaign?.is_private)
    const getResponses = createGetRequestErrors([STATE_KEYS.UpdateSubmissionPerformance])
    const response = useSelector(getResponses)[STATE_KEYS.UpdateSubmissionPerformance]
    const extraColumns = useSelector(selectTasksPayoutColumns(!hasShowPrivateCampaignBudget, currencySign))
    const selectCampaignPerformanceData = useMemo(makeSelectCampaignPerformanceData, [])
    const data = useSelector(selectCampaignPerformanceData)
    const activePageNum = useSelector(selectActivePageNum)
    const searchTerm = useSelector(selectSearchTerm)
    const partiposterTags = useSelector(selectPartiposterTagsParam)
    const hasPayPartipostersPermission = usePermission(Payout.PayPartiposters)
    const isAbleToViewPayoutPage = useCheckSomePermissions([
        Payout.PayPartiposters,
        Payout.DisqualifyPartiposters,
        Payout.UpdatePayouts,
    ])
    const canManageTag = usePermission(CampaignSubmission.ManageTag)
    const [adjustEarningContext, setAdjustEarningContext] = useState({
        userName: '',
        earning: 0,
        submissionUuid: '',
        reason: '',
        custom_reason: '',
        additional_info: '',
        adjustment_source: '',
    })
    const [paidOutsideAppContext, setPaidOutsideAppContext] = useState({
        earning: 0,
        submissionUuid: '',
    })

    useEffect(() => {
        if (response && response.hasOwnProperty('uuid')) {
            // updated successfully
            if (response.uuid === editingSubmissionPerformance.submissionUuid) {
                setEditingSubmissionPerformance({
                    submissionUuid: '',
                    data: [],
                })
                dispatch(
                    clearResponse({
                        stateKey: STATE_KEYS.UpdateSubmissionPerformance,
                    }),
                )
            }
        }
    }, [response, editingSubmissionPerformance])

    useEffect(() => {
        const offset: number = activePageNum * MAX_PAGE_SIZE

        if (campaignUuid && !isLoading) {
            dispatch(
                getCampaign({
                    uuid: campaignUuid as string,
                    onSuccess: campaign => {
                        dispatch(setTeamCountryByCampaign(campaign.uuid))
                    },
                }),
            )
            dispatch(
                getCampaignPerformance({
                    uuid: campaignUuid as string,
                    offset,
                    keywords: searchTerm,
                    tags: partiposterTags,
                }),
            )
            dispatch(getCampaignPerformanceFiltersRequest({ uuid: campaignUuid as string }))
            dispatch(getTasksList({ campaignUuid: campaignUuid as string }))
        }
        return () => {
            dispatch(clearTasksList())
            dispatch(clearCampaignPerformance())
            dispatch(clearSubmissionFilters())
        }
    }, [campaignUuid, activePageNum, searchTerm, partiposterTags])

    useEffect(() => {
        if (campaign) dispatch(requestProfileOptions({ country: campaign.country }))
    }, [campaign])

    const cancelEditPerformance = () => {
        setEditingSubmissionPerformance({ submissionUuid: '', data: [] })
    }

    const savePerformance = () => {
        dispatch(
            updateSubmissionPerformance({
                campaignUuid: campaign.uuid,
                submissionUuid: editingSubmissionPerformance.submissionUuid,
                submissionTasks: editingSubmissionPerformance.data,
            }),
        )
    }

    const payPartiposter = (submissionUuid: string) => {
        dispatch(
            paySubmission({
                campaignUuid: campaign.uuid,
                submissionUuid,
            }),
        )
    }

    const disqualifyPartiposter = (submissionUuid: string) => {
        dispatch(
            rejectSubmissionPayout({
                campaignUuid: campaign.uuid,
                submissionUuid,
            }),
        )
    }
    const editPerformance = (submissionUuid: string) => {
        const perf = campaignPerformance.filter((performance: any) => performance.uuid === submissionUuid)
        if (perf.length > 0) {
            setEditingSubmissionPerformance({
                submissionUuid,
                data: _.cloneDeep(perf[0].submission_tasks),
            })
        }
    }

    const getPerformance = (taskUuid: string, performanceKey: string) => {
        const { data } = editingSubmissionPerformance
        const taskData = data.filter((task: any) => task.task_uuid === taskUuid)[0]

        if (taskData.performance.hasOwnProperty(performanceKey)) {
            return taskData.performance[performanceKey].count
        } else {
            return taskData.performance.extras[performanceKey].count
        }
    }

    const handlePerformanceUpdate = (event: any, taskUuid: string, performanceKey: string) => {
        const value = parseInt(event.target.value)
        setEditingSubmissionPerformance((prevState: any) => {
            return {
                submissionUuid: prevState.submissionUuid,
                data: prevState.data.map((task: any) => {
                    if (task.task_uuid !== taskUuid) return task
                    if (task.performance.hasOwnProperty(performanceKey)) {
                        task.performance[performanceKey].count = value
                    } else {
                        task.performance.extras[performanceKey].count = value
                    }
                    return task
                }),
            }
        })
    }

    const handleClickExport = () => {
        if (campaign && campaign.uuid) dispatch(exportCampaignPerformance({ uuid: campaign.uuid }))
    }

    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return campaignPlatforms.includes(platform)
    }

    const columns: GridColDef[] = useMemo(() => {
        const columns: GridColDefProps[] = [
            {
                field: 'action',
                headerName: t('submission.table.actionsLabel'),
                width: 170,
                minWidth: 170,
                renderCell: (props: GridRenderCellParams) => <PerformanceActionCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...(hasShowPrivateCampaignBudget
                ? [
                      {
                          field: 'totalEarnings',
                          headerName: t('submission.table.totalEarningsLabel'),
                          minWidth: 160,
                          width: 160,
                          renderCell: (props: GridRenderCellParams) => <EarningAdjustmentCell {...props} />,
                          sortable: false,
                          filterable: false,
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 224,
                              description: t('submission.table.totalEarningsLabel.tooltips'),
                              options: {
                                  mr: 0,
                              },
                          }),
                      },
                  ]
                : []),
            {
                field: 'profile',
                type: 'string',
                headerName: t('submission.table.profile'),
                width: 160,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
                subFields: [
                    {
                        field: 'name',
                        headerName: t('submission.table.nameLabel'),
                    },
                    {
                        field: 'gender',
                        headerName: t('submission.table.genderLabel'),
                    },
                    {
                        field: 'age',
                        headerName: t('submission.table.ageLabel'),
                    },
                ],
            },
            {
                field: 'socialMediaInfo',
                type: 'string',
                headerName: t('submission.table.socialMediaInfo'),
                width: 180,
                minWidth: 60,
                sortable: false,
                renderCell: (props: GridRenderCellParams) => <SocialMediaInfoCell {...props} />,
                subFields: [
                    ...(hasPlatform(PLATFORM_ENUM.IG)
                        ? [
                              {
                                  field: FIELDS_TYPE.IG_FOLLOWERS,
                                  headerName: t('submission.table.instagramFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.IG_USERNAME,
                                  headerName: t('submission.table.instagramUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.FB)
                        ? [
                              {
                                  field: FIELDS_TYPE.FB_FRIENDS,
                                  headerName: t('submission.table.facebookFriendsLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.FB_USERNAME,
                                  headerName: t('submission.table.facebookUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                        ? [
                              {
                                  field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                                  headerName: t('submission.table.tikTokFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TIKTOK_USERNAME,
                                  headerName: t('submission.table.tikTokUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                        ? [
                              {
                                  field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                                  headerName: t('submission.table.twitterFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.TWITTER_USERNAME,
                                  headerName: t('submission.table.twitterUsernameLabel'),
                              },
                          ]
                        : []),
                    ...(hasPlatform(PLATFORM_ENUM.XIAOHONGSHU)
                        ? [
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
                                  headerName: t('submission.table.XHSFollowersLabel'),
                              },
                              {
                                  field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                                  headerName: t('submission.table.XHSUsernameLabel'),
                              },
                          ]
                        : []),
                ],
            },

            ...(campaignPlatforms.includes(PLATFORM_ENUM.IG)
                ? [
                      {
                          field: 'instagramFollowerCount',
                          headerName: t('submission.table.instagramFollowerCountLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 255,
                              title: t('submission.table.followerCountLabel'),
                              description: t('submission.table.followerCountTooltip'),
                              options: { mr: 0 },
                          }),
                          width: 255,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(campaignPlatforms.includes(PLATFORM_ENUM.FB)
                ? [
                      {
                          field: 'facebookFollowerCount',
                          headerName: t('submission.table.facebookFollowerCountLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 253,
                              title: t('submission.table.followerCountLabel'),
                              description: t('submission.table.followerCountTooltip'),
                              options: { mr: 0 },
                          }),
                          width: 253,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(campaignPlatforms.includes(PLATFORM_ENUM.TIKTOK)
                ? [
                      {
                          field: 'tiktokFollowerCount',
                          headerName: t('submission.table.tikTokFollowerCountLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 277,
                              title: t('submission.table.followerCountLabel'),
                              description: t('submission.table.followerCountTooltip'),
                              options: { mr: 0 },
                          }),
                          width: 277,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(campaignPlatforms.includes(PLATFORM_ENUM.TWITTER)
                ? [
                      {
                          field: 'twitterFollowerCount',
                          headerName: t('submission.table.twitterFollowerCountLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 285,
                              title: t('submission.table.followerCountLabel'),
                              description: t('submission.table.followerCountTooltip'),
                              options: { mr: 0 },
                          }),
                          width: 285,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            ...(campaignPlatforms.includes(PLATFORM_ENUM.XIAOHONGSHU)
                ? [
                      {
                          field: 'xiaohongshuFollowerCount',
                          headerName: t('submission.table.XHSFollowersCountLabel'),
                          renderHeader: getInfoTooltipHeaderCell({
                              width: 285,
                              title: t('submission.table.followerCountLabel'),
                              description: t('submission.table.followerCountTooltip'),
                              options: { mr: 0 },
                          }),
                          width: 285,
                          renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                          hide: false,
                          sortable: false,
                          filterable: false,
                      },
                  ]
                : []),
            {
                field: 'payout_remark',
                headerName: t('submission.table.additionalInfoLabel'),
                minWidth: 200,
                width: 200,
                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                sortable: false,
                filterable: false,
            },
            ...extraColumns,
        ]
        return canManageTag
            ? columns.concat([
                  {
                      field: 'partiposterTags',
                      headerName: t('submission.table.partiposterTagsLabel'),
                      width: 240,
                      minWidth: 200,
                      renderCell: (props: GridRenderCellParams) => <TagCell {...props} />,
                      sortable: false,
                      filterable: false,
                  },
              ])
            : columns
    }, [extraColumns, campaignPlatforms, canManageTag])

    interface Row {
        [key: string]:
            | {
                  value: number
                  editingSubmissionPerformance: {
                      submissionUuid: string
                      data: any[]
                  }
                  getPerformance: any
                  handlePerformanceUpdate: any
                  taskUuid: string
                  performanceKey: string
              }
            | { value: string }
    }

    const getExtraRows = (taskPerformances: any) => {
        const perfArr = Object.keys(taskPerformances).map((taskUuid: string) => {
            const performanceData = taskPerformances[taskUuid]
            const rowsArr = Object.keys(performanceData).map((performanceKey: string) => {
                const rowObj: Row = {}
                const rowKey = taskUuid + '_' + performanceKey
                rowObj[rowKey] = {
                    value: performanceData[performanceKey].count,
                    editingSubmissionPerformance: editingSubmissionPerformance,
                    getPerformance: getPerformance,
                    handlePerformanceUpdate: handlePerformanceUpdate,
                    taskUuid: taskUuid,
                    performanceKey: performanceKey,
                }
                return rowObj
            })
            const taskTypeRow: Row = {}
            const task = tasks.find((task: any) => task.uuid === taskUuid)
            taskTypeRow[taskUuid + '_taskName'] = { value: task ? task.title : '' }
            rowsArr.push(taskTypeRow)
            return rowsArr.flat()
        })
        return perfArr.flat()
    }

    const rows = useMemo(
        () =>
            data.map(performance => {
                const { partiposter_profile } = performance
                const { social_media_accounts: socialMediaAccounts, name } = partiposter_profile
                const igProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.IG)
                const fbProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.FB)
                const tiktokProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TIKTOK)
                const twitterProfile = socialMediaAccounts.find(account => account.platform === PLATFORM_ENUM.TWITTER)
                const xiaohongshuProfile = socialMediaAccounts.find(
                    account => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
                )
                const extraRows = getExtraRows(performance.taskPerformances)

                return Object.assign(
                    {
                        id: performance.uuid,
                        profile: { profile: partiposter_profile },
                        socialMediaInfo: {
                            profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile],
                        },
                        instagramFollowerCount: { value: performance.instagram_follower_count },
                        facebookFollowerCount: { value: performance.facebook_follower_count },
                        tiktokFollowerCount: { value: performance.tiktok_follower_count },
                        twitterFollowerCount: { value: performance.twitter_follower_count },
                        xiaohongshuFollowerCount: { value: performance.xiaohongshu_follower_count },
                        youtubeFollowerCount: { value: performance.youtube_follower_count },
                        totalEarnings: {
                            earning: performance.totalEarnings,
                            earning_before_adjustment: performance.total_earnings_without_adjustment,
                            adjustment_reason:
                                performance.earnings_adjustment_reason === 'others'
                                    ? performance.earnings_adjustment_custom_reason
                                    : campaignSetting?.submission_earnings_adjustment_reasons?.find(
                                          reason => reason.id === performance.earnings_adjustment_reason,
                                      )?.label || '',

                            onClickEdit:
                                performance.paymentStatus === SUBMISSION_STATUS_ENUM.FINISHED
                                    ? () =>
                                          setAdjustEarningContext({
                                              earning: performance.totalEarnings,
                                              userName: name,
                                              submissionUuid: performance.uuid,
                                              additional_info: performance.payout_remark || '',
                                              custom_reason: performance.earnings_adjustment_custom_reason || '',
                                              adjustment_source: performance.earnings_adjustment_source || '',
                                              reason: performance.earnings_adjustment_reason || '',
                                          })
                                    : undefined,
                        },
                        payout_remark: {
                            value: performance.payout_remark,
                        },
                        action: {
                            status: {
                                incomplete: t('statusLabel.incompleteSubmission'),
                                rejected: t('statusLabel.rejected'),
                                paid: t('statusLabel.paid'),
                                closed: t('statusLabel.closed'),
                                paidOutsideApp: t('statusLabel.paidOutsideApp'),
                            },
                            isPaidOutsideApp: performance.is_paid_outside_app,
                            paidOutsideAppReason:
                                performance.paid_outside_app_reason === 'others'
                                    ? performance.paid_outside_app_custom_reason
                                    : t(
                                          campaignSetting?.submission_paid_outside_app_reasons?.find(
                                              reason => reason.id === performance.paid_outside_app_reason,
                                          )?.label || '',
                                      ),
                            isUpdateLoading: isUpdateLoading,
                            editingSubmissionPerformance: editingSubmissionPerformance,
                            paymentStatus: performance.paymentStatus,
                            savePerformance: savePerformance,
                            cancelEditPerformance: cancelEditPerformance,
                            payPartiposter: payPartiposter,
                            disqualifyPartiposter: disqualifyPartiposter,
                            editPerformance: editPerformance,
                            paidOutsideApp: () => {
                                setPaidOutsideAppContext({
                                    earning: performance.totalEarnings,
                                    submissionUuid: performance.uuid,
                                })
                            },
                        },
                        uuid: performance.uuid,
                        partiposterTags: {
                            profileData: partiposter_profile,
                            submissionUuid: performance.uuid,
                            campaignUuid: campaignUuid,
                        },
                    },
                    ...extraRows,
                )
            }),
        [campaignUuid, data, editingSubmissionPerformance, isUpdateLoading, campaign],
    )
    const massPayoutInfo = useMemo(() => {
        const massPayoutData: MassPayoutData[] = []
        rows.map(submission => {
            if (submission.igUsername) {
                const submissionData = {
                    name: submission.igUsername.account,
                    submissionId: submission.uuid,
                }
                massPayoutData.push(submissionData)
            }
        })
        return massPayoutData
    }, [rows])

    useEffect(() => {
        return () => {
            dispatch(setPartiposterTagFilter([]))
            handleClosePartiposterTags()
        }
    }, [])

    const enableImportExport = rows.length > 0 && hasPayPartipostersPermission

    const closeEarningModal = () => {
        setAdjustEarningContext({
            userName: '',
            earning: 0,
            submissionUuid: '',
            additional_info: '',
            custom_reason: '',
            adjustment_source: '',
            reason: '',
        })
    }

    const closePaidOutsideAppModal = () => {
        setPaidOutsideAppContext({
            earning: 0,
            submissionUuid: '',
        })
    }

    const refreshTable = () => {
        const offset: number = activePageNum * MAX_PAGE_SIZE
        dispatch(
            getCampaignPerformance({
                uuid: campaignUuid as string,
                offset,
                keywords: searchTerm,
                tags: partiposterTags,
            }),
        )
    }

    if (!campaignUuid) return <Redirect to="/submissions" />
    if (profileUuid && !isAbleToViewPayoutPage) return <Redirect to="/403" />
    return (
        <>
            <Box mx={theme.spacing.xs} mt={theme.spacing.xs}>
                {campaign ? (
                    <PageHeader title={campaign.title} subTitle={t('submission.updatePayouts.pageSubtitle')} />
                ) : null}
                <PartiTable
                    rowHeight={84}
                    getRowHeight={() => 'auto'}
                    columns={columns}
                    rows={rows}
                    isLoading={isLoading}
                    viewType={TableViewType.PERFORMANCE}
                    hideToolbar={false}
                    enableExport={enableImportExport}
                    data-testid="payouts-table"
                    enableImportList={enableImportExport}
                    showFilter
                />
                {campaign && (
                    <ListApprovePayoutPopUp campaignUuid={campaign.uuid} massApprovePayoutInfo={massPayoutInfo} />
                )}
            </Box>
            <TagHistoryModal />
            <TagAddModal />
            <DeleteTagModal />
            <ExportModal
                tableTitle={t('submission.exportList.partipostPayoutsTableTitle')}
                DescriptionComponent={
                    <>
                        <Text color={theme.colors.mineShaft} mx={theme.spacing.xxxs} mb={theme.spacing.xs}>
                            {t('modalWindow.exportPayoutListSubtitle')}
                        </Text>
                        <Text color={theme.colors.mineShaft} mx={theme.spacing.xxxs}>
                            {t('modalWindow.exportPayoutListInstructionSubtitle')}
                        </Text>
                    </>
                }
                isRequesting={isExportRequesting}
                onClickExport={handleClickExport}
            />

            {adjustEarningContext.userName && (
                <AdjustEarningModal
                    campaignUuid={campaign.uuid}
                    submissionUuid={adjustEarningContext.submissionUuid}
                    userName={adjustEarningContext.userName}
                    currencySign={currencySign}
                    addition_info={adjustEarningContext.additional_info}
                    custom_reason={adjustEarningContext.custom_reason}
                    earnings_adjustment_source={adjustEarningContext.adjustment_source}
                    reason={adjustEarningContext.reason}
                    oldEarning={adjustEarningContext.earning}
                    onClose={closeEarningModal}
                    onSubmit={refreshTable}
                />
            )}

            {paidOutsideAppContext.submissionUuid && (
                <PaidOutsideAppModal
                    currencySign={currencySign}
                    earning={paidOutsideAppContext.earning}
                    onClose={closePaidOutsideAppModal}
                    submissionUuid={paidOutsideAppContext.submissionUuid}
                    country={campaign.country}
                    campaignUuid={campaign.uuid}
                    onSubmit={refreshTable}
                />
            )}
        </>
    )
}

export default Payouts
